import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { toast } from "react-toastify";

import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { openApi } from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(8),
    marginLeft: 5,
  },
  hero: {
    marginBottom: theme.spacing(3),
  },
  heroMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  sidePaper: {
    padding: theme.spacing(2),
    position: "sticky",
    top: theme.spacing(2),
  },
  navGroup: {
    marginTop: theme.spacing(2),
  },
  navLink: {
    display: "block",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    cursor: "pointer",
  },
  contentColumn: {
    display: "grid",
    gap: theme.spacing(2),
  },
  endpointCard: {
    padding: theme.spacing(2),
    scrollMarginTop: theme.spacing(10),
  },
  endpointHeader: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  methodChip: {
    minWidth: 72,
    fontWeight: 700,
  },
  section: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  pre: {
    margin: 0,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    overflowX: "auto",
    fontSize: 12,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  monoLine: {
    display: "block",
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    fontFamily: "monospace",
    fontSize: 13,
    overflowX: "auto",
  },
  table: {
    "& td, & th": {
      verticalAlign: "top",
    },
  },
  smallText: {
    color: theme.palette.text.secondary,
  },
  responseBlock: {
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(3),
  },
}));

const METHOD_COLORS = {
  get: "#2e7d32",
  post: "#1565c0",
  put: "#ef6c00",
  patch: "#6a1b9a",
  delete: "#c62828",
};

const HTTP_METHODS = ["get", "post", "put", "patch", "delete"];

const formatJson = (value) => JSON.stringify(value, null, 2);

const getMethodColor = (method) => METHOD_COLORS[method] || "#455a64";

const makeEndpointId = (method, path) =>
  `${method}-${path.replace(/[^a-zA-Z0-9]+/g, "-")}`;

const getLastRefSegment = (ref) => {
  if (!ref) return "";
  const segments = ref.split("/");
  return segments[segments.length - 1] || "";
};

const resolveRef = (spec, ref) => {
  if (!ref || ref.indexOf("#/") !== 0) {
    return null;
  }

  return ref
    .replace(/^#\//, "")
    .split("/")
    .reduce((current, segment) => {
      if (!current) return null;
      return current[segment];
    }, spec);
};

const resolveParameter = (spec, parameter) => {
  if (!parameter) return null;
  if (!parameter.$ref) return parameter;
  return resolveRef(spec, parameter.$ref);
};

const resolveSchema = (spec, schema) => {
  if (!schema) return null;
  if (schema.$ref) {
    return resolveRef(spec, schema.$ref);
  }
  return schema;
};

const getTypeLabel = (schema) => {
  if (!schema) return "object";
  if (Array.isArray(schema.type)) {
    return schema.type.join(" | ");
  }
  if (schema.type) {
    return schema.type;
  }
  if (schema.enum) {
    return "enum";
  }
  if (schema.oneOf) {
    return schema.oneOf
      .map((item) => getTypeLabel(item))
      .filter(Boolean)
      .join(" | ");
  }
  if (schema.anyOf) {
    return schema.anyOf
      .map((item) => getTypeLabel(item))
      .filter(Boolean)
      .join(" | ");
  }
  if (schema.allOf) {
    return "object";
  }
  if (schema.properties) {
    return "object";
  }
  if (schema.items) {
    return "array";
  }
  return "object";
};

const buildSchemaPreview = (spec, schema, depth = 0) => {
  const resolvedSchema = resolveSchema(spec, schema);

  if (!resolvedSchema) {
    return null;
  }

  if (depth > 4) {
    return "[Object]";
  }

  if (resolvedSchema.example !== undefined) {
    return resolvedSchema.example;
  }

  if (resolvedSchema.examples && Array.isArray(resolvedSchema.examples) && resolvedSchema.examples.length > 0) {
    return resolvedSchema.examples[0];
  }

  if (resolvedSchema.enum && resolvedSchema.enum.length > 0) {
    return resolvedSchema.enum[0];
  }

  if (resolvedSchema.oneOf && resolvedSchema.oneOf.length > 0) {
    return buildSchemaPreview(spec, resolvedSchema.oneOf[0], depth + 1);
  }

  if (resolvedSchema.anyOf && resolvedSchema.anyOf.length > 0) {
    return buildSchemaPreview(spec, resolvedSchema.anyOf[0], depth + 1);
  }

  if (resolvedSchema.allOf && resolvedSchema.allOf.length > 0) {
    const merged = resolvedSchema.allOf.reduce((accumulator, item) => {
      const preview = buildSchemaPreview(spec, item, depth + 1);
      if (preview && typeof preview === "object" && !Array.isArray(preview)) {
        return { ...accumulator, ...preview };
      }
      return accumulator;
    }, {});
    return Object.keys(merged).length > 0 ? merged : {};
  }

  if (resolvedSchema.type === "array" || resolvedSchema.items) {
    return [buildSchemaPreview(spec, resolvedSchema.items, depth + 1)];
  }

  if (resolvedSchema.type === "object" || resolvedSchema.properties) {
    return Object.entries(resolvedSchema.properties || {}).reduce(
      (accumulator, [key, value]) => ({
        ...accumulator,
        [key]: buildSchemaPreview(spec, value, depth + 1),
      }),
      {}
    );
  }

  if (Array.isArray(resolvedSchema.type)) {
    const firstType = resolvedSchema.type.find((item) => item !== "null");
    return buildSchemaPreview(spec, { ...resolvedSchema, type: firstType }, depth + 1);
  }

  switch (resolvedSchema.type) {
    case "integer":
    case "number":
      return 0;
    case "boolean":
      return false;
    case "string":
      return "";
    default:
      return null;
  }
};

const getContentEntries = (content) => Object.entries(content || {});

const getRequestExamples = (spec, requestBody) => {
  if (!requestBody) return null;

  const resolvedRequestBody = requestBody.$ref ? resolveRef(spec, requestBody.$ref) : requestBody;
  const [contentType, contentValue] = getContentEntries(resolvedRequestBody?.content)[0] || [];

  if (!contentValue) {
    return null;
  }

  if (contentValue.example !== undefined) {
    return [{ label: "Exemplo", contentType, value: contentValue.example }];
  }

  const examples = Object.entries(contentValue.examples || {}).map(([label, example]) => ({
    label,
    contentType,
    value: example.value,
  }));

  if (examples.length > 0) {
    return examples;
  }

  const preview = buildSchemaPreview(spec, contentValue.schema);
  if (preview !== null) {
    return [{ label: "Schema preview", contentType, value: preview }];
  }

  return null;
};

const getResponseExamples = (spec, response) => {
  const resolvedResponse = response?.$ref ? resolveRef(spec, response.$ref) : response;
  const [contentType, contentValue] = getContentEntries(resolvedResponse?.content)[0] || [];

  if (!contentValue) {
    return [];
  }

  if (contentValue.example !== undefined) {
    return [{ label: "Exemplo", contentType, value: contentValue.example }];
  }

  const examples = Object.entries(contentValue.examples || {}).map(([label, example]) => ({
    label,
    contentType,
    value: example.value,
  }));

  if (examples.length > 0) {
    return examples;
  }

  const preview = buildSchemaPreview(spec, contentValue.schema);
  if (preview !== null && preview !== undefined) {
    return [{ label: "Schema preview", contentType, value: preview }];
  }

  return [];
};

const getSecurityRequirements = (operationSecurity, rootSecurity) => {
  if (Array.isArray(operationSecurity)) {
    return operationSecurity;
  }

  if (Array.isArray(rootSecurity)) {
    return rootSecurity;
  }

  return [];
};

const describeSecurityRequirement = (spec, requirement) => {
  const [schemeName] = Object.keys(requirement || {});
  if (!schemeName) {
    return null;
  }

  const scheme = spec?.components?.securitySchemes?.[schemeName];
  if (!scheme) {
    return schemeName;
  }

  const type = [scheme.type, scheme.scheme].filter(Boolean).join(" ");
  return [schemeName, type].filter(Boolean).join(" | ");
};

const describeSchema = (spec, schema) => {
  const resolvedSchema = resolveSchema(spec, schema);
  if (!resolvedSchema) {
    return "Schema indisponivel";
  }

  const parts = [getTypeLabel(resolvedSchema)];

  if (resolvedSchema.enum) {
    parts.push(`enum: ${resolvedSchema.enum.join(", ")}`);
  }

  if (resolvedSchema.items) {
    parts.push(`items: ${getTypeLabel(resolveSchema(spec, resolvedSchema.items))}`);
  }

  if (resolvedSchema.$ref) {
    parts.push(getLastRefSegment(resolvedSchema.$ref));
  }

  return parts.join(" | ");
};

const flattenSchemaProperties = (spec, schema, prefix = "") => {
  const resolvedSchema = resolveSchema(spec, schema);

  if (!resolvedSchema) {
    return [];
  }

  if (resolvedSchema.oneOf) {
    return resolvedSchema.oneOf.flatMap((item) => flattenSchemaProperties(spec, item, prefix));
  }

  if (resolvedSchema.anyOf) {
    return resolvedSchema.anyOf.flatMap((item) => flattenSchemaProperties(spec, item, prefix));
  }

  if (resolvedSchema.allOf) {
    return resolvedSchema.allOf.flatMap((item) => flattenSchemaProperties(spec, item, prefix));
  }

  if (!resolvedSchema.properties) {
    return [];
  }

  const requiredFields = new Set(resolvedSchema.required || []);

  return Object.entries(resolvedSchema.properties).flatMap(([name, propertySchema]) => {
    const resolvedProperty = resolveSchema(spec, propertySchema);
    const fieldName = prefix ? `${prefix}.${name}` : name;
    const currentField = {
      name: fieldName,
      type: describeSchema(spec, propertySchema),
      required: requiredFields.has(name),
      description:
        resolvedProperty?.description ||
        (resolvedProperty?.$ref ? getLastRefSegment(resolvedProperty.$ref) : ""),
    };

    const nestedFields =
      resolvedProperty &&
      (resolvedProperty.properties || resolvedProperty.oneOf || resolvedProperty.anyOf || resolvedProperty.allOf)
        ? flattenSchemaProperties(spec, propertySchema, fieldName)
        : [];

    return [currentField, ...nestedFields];
  });
};

const buildEndpointList = (spec) => {
  if (!spec?.paths) return [];

  return Object.entries(spec.paths).flatMap(([path, methods]) =>
    HTTP_METHODS.filter((method) => methods[method]).map((method) => {
      const operation = methods[method];
      const parameters = (operation.parameters || [])
        .map((parameter) => resolveParameter(spec, parameter))
        .filter(Boolean);
      const tag = (operation.tags && operation.tags[0]) || "Outros";

      return {
        id: makeEndpointId(method, path),
        method,
        path,
        tag,
        summary: operation.summary || `${method.toUpperCase()} ${path}`,
        description: operation.description || "",
        parameters,
        requestBody: operation.requestBody || null,
        responses: operation.responses || {},
        security: getSecurityRequirements(operation.security, spec.security),
      };
    })
  );
};

const groupEndpointsByTag = (spec, endpoints) => {
  const tagDescriptions = (spec.tags || []).reduce((accumulator, tag) => {
    accumulator[tag.name] = tag.description || "";
    return accumulator;
  }, {});

  const groups = endpoints.reduce((accumulator, endpoint) => {
    if (!accumulator[endpoint.tag]) {
      accumulator[endpoint.tag] = [];
    }
    accumulator[endpoint.tag].push(endpoint);
    return accumulator;
  }, {});

  return Object.keys(groups).map((tag) => ({
    tag,
    description: tagDescriptions[tag] || "",
    endpoints: groups[tag].sort((left, right) => left.path.localeCompare(right.path)),
  }));
};

const SchemaTable = ({ rows }) => {
  const classes = useStyles();

  if (!rows.length) {
    return (
      <Typography variant="body2" className={classes.smallText}>
        Nenhum campo documentado.
      </Typography>
    );
  }

  return (
    <Table size="small" className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell>Campo</TableCell>
          <TableCell>Tipo</TableCell>
          <TableCell>Obrigatorio</TableCell>
          <TableCell>Descricao</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.name}-${row.type}`}>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.type}</TableCell>
            <TableCell>{row.required ? "Sim" : "Nao"}</TableCell>
            <TableCell>{row.description || "-"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [loading, setLoading] = useState(true);
  const [spec, setSpec] = useState(null);
  const [specSource, setSpecSource] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadPage = async () => {
      try {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);

        if (!planConfigs.plan.useExternalApi) {
          toast.error("Esta empresa nao possui permissao para acessar essa pagina. Estamos lhe redirecionando.");
          setTimeout(() => {
            history.push("/");
          }, 1000);
          return;
        }

        let data;
        let source = "frontend";

        try {
          const localSpecResponse = await fetch("/openapi.json");

          if (!localSpecResponse.ok) {
            throw new Error(`Local spec returned ${localSpecResponse.status}`);
          }

          data = await localSpecResponse.json();
        } catch (localError) {
          const remoteSpecResponse = await openApi.request({
            url: "/openapi.json",
            method: "GET",
          });

          data = remoteSpecResponse.data;
          source = "backend";
        }

        if (isMounted) {
          setSpec(data);
          setSpecSource(source);
        }
      } catch (error) {
        toastError(error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, user.companyId]);

  const endpoints = useMemo(() => buildEndpointList(spec), [spec]);
  const groupedEndpoints = useMemo(() => groupEndpointsByTag(spec || {}, endpoints), [spec, endpoints]);

  if (loading) {
    return (
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container justifyContent="center" alignItems="center">
          <CircularProgress size={28} />
        </Grid>
      </Paper>
    );
  }

  if (!spec) {
    return (
      <Paper className={classes.mainPaper} variant="outlined">
        <Alert severity="error">
          Nao foi possivel carregar o OpenAPI do backend. Verifique se `openapi.json` esta sendo servido em
          `${process.env.REACT_APP_BACKEND_URL}/openapi.json`.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <div className={classes.hero}>
        <Typography variant="h4">{spec.info?.title || "API Documentation"}</Typography>
        <Typography variant="body1" className={classes.smallText}>
          {spec.info?.description || "Documentacao carregada dinamicamente a partir do backend."}
        </Typography>
        <div className={classes.heroMeta}>
          <Chip label={`OpenAPI ${spec.openapi || "-"}`} size="small" />
          <Chip label={`Versao ${spec.info?.version || "-"}`} size="small" />
          <Chip label={`${endpoints.length} endpoints`} size="small" />
          <Chip label={`Origem ${specSource === "frontend" ? "frontend/public" : "backend"}`} size="small" />
        </div>
      </div>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" className={classes.sidePaper}>
            <Typography variant="h6">Endpoints</Typography>
            <Typography variant="body2" className={classes.smallText}>
              A lista abaixo vem do `openapi.json` carregado pela pagina.
            </Typography>

            {groupedEndpoints.map((group) => (
              <div key={group.tag} className={classes.navGroup}>
                <Typography variant="subtitle2">{group.tag}</Typography>
                {group.endpoints.map((endpoint) => (
                  <Link
                    key={endpoint.id}
                    href={`#${endpoint.id}`}
                    underline="none"
                    className={classes.navLink}
                  >
                    <strong>{endpoint.method.toUpperCase()}</strong> {endpoint.path}
                  </Link>
                ))}
              </div>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9} className={classes.contentColumn}>
          {spec.servers?.length > 0 && (
            <Paper variant="outlined" className={classes.endpointCard}>
              <Typography variant="h6">Base URLs</Typography>
              <div className={classes.section}>
                {spec.servers.map((server) => (
                  <div key={`${server.url}-${server.description || ""}`} className={classes.responseBlock}>
                    <Typography className={classes.monoLine}>{server.url}</Typography>
                    {server.description && (
                      <Typography variant="body2" className={classes.smallText}>
                        {server.description}
                      </Typography>
                    )}
                  </div>
                ))}
              </div>
            </Paper>
          )}

          {spec.components?.securitySchemes && Object.keys(spec.components.securitySchemes).length > 0 && (
            <Paper variant="outlined" className={classes.endpointCard}>
              <Typography variant="h6">Autenticacao</Typography>
              <div className={classes.contentColumn}>
                {Object.entries(spec.components.securitySchemes).map(([name, scheme]) => (
                  <div key={name}>
                    <Typography variant="subtitle1">{name}</Typography>
                    <Typography variant="body2" className={classes.smallText}>
                      {[scheme.type, scheme.scheme, scheme.bearerFormat].filter(Boolean).join(" | ") || "-"}
                    </Typography>
                    {scheme.description && <Typography variant="body2">{scheme.description}</Typography>}
                  </div>
                ))}
              </div>
            </Paper>
          )}

          {groupedEndpoints.length === 0 && (
            <Paper variant="outlined" className={classes.emptyState}>
              <Typography variant="h6">Nenhum endpoint encontrado</Typography>
              <Typography variant="body2" className={classes.smallText}>
                O arquivo OpenAPI foi carregado, mas nao possui operacoes em `paths`.
              </Typography>
            </Paper>
          )}

          {groupedEndpoints.map((group) => (
            <div key={group.tag}>
              <Typography variant="h5">{group.tag}</Typography>
              {group.description && (
                <Typography variant="body2" className={classes.smallText}>
                  {group.description}
                </Typography>
              )}

              <div className={classes.contentColumn}>
                {group.endpoints.map((endpoint) => {
                  const requestExamples = getRequestExamples(spec, endpoint.requestBody) || [];
                  const requestSchemaRows = flattenSchemaProperties(
                    spec,
                    getContentEntries(
                      (endpoint.requestBody?.$ref
                        ? resolveRef(spec, endpoint.requestBody.$ref)
                        : endpoint.requestBody
                      )?.content
                    )[0]?.[1]?.schema
                  );

                  return (
                    <Paper
                      key={endpoint.id}
                      id={endpoint.id}
                      variant="outlined"
                      className={classes.endpointCard}
                    >
                      <div className={classes.endpointHeader}>
                        <Chip
                          label={endpoint.method.toUpperCase()}
                          size="small"
                          className={classes.methodChip}
                          style={{
                            backgroundColor: getMethodColor(endpoint.method),
                            color: "#fff",
                          }}
                        />
                        <Typography variant="h6">{endpoint.summary}</Typography>
                      </div>

                      <Typography className={classes.monoLine}>{endpoint.path}</Typography>

                      {endpoint.description && (
                        <div className={classes.section}>
                          <Typography variant="body2">{endpoint.description}</Typography>
                        </div>
                      )}

                      {endpoint.security.length > 0 && (
                        <div className={classes.section}>
                          <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Seguranca
                          </Typography>
                          {endpoint.security.map((requirement, index) => (
                            <Typography key={`${endpoint.id}-security-${index}`} variant="body2" className={classes.smallText}>
                              {describeSecurityRequirement(spec, requirement)}
                            </Typography>
                          ))}
                        </div>
                      )}

                      <div className={classes.section}>
                        <Typography variant="subtitle1" className={classes.sectionTitle}>
                          Parametros
                        </Typography>
                        {endpoint.parameters.length > 0 ? (
                          <Table size="small" className={classes.table}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Em</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Obrigatorio</TableCell>
                                <TableCell>Descricao</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {endpoint.parameters.map((parameter) => (
                                <TableRow key={`${endpoint.id}-${parameter.name}`}>
                                  <TableCell>{parameter.name}</TableCell>
                                  <TableCell>{parameter.in}</TableCell>
                                  <TableCell>{describeSchema(spec, parameter.schema)}</TableCell>
                                  <TableCell>{parameter.required ? "Sim" : "Nao"}</TableCell>
                                  <TableCell>{parameter.description || "-"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" className={classes.smallText}>
                            Este endpoint nao possui parametros documentados.
                          </Typography>
                        )}
                      </div>

                      {endpoint.requestBody && (
                        <div className={classes.section}>
                          <Divider />
                          <Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginTop: 16 }}>
                            Request Body
                          </Typography>

                          <Typography variant="body2" className={classes.smallText}>
                            Obrigatorio: {endpoint.requestBody.required ? "Sim" : "Nao"}
                          </Typography>

                          {requestExamples[0]?.contentType && (
                            <Typography variant="body2" className={classes.smallText}>
                              Content-Type: {requestExamples[0].contentType}
                            </Typography>
                          )}

                          <SchemaTable rows={requestSchemaRows} />

                          {requestExamples.map((example) => (
                            <div key={`${endpoint.id}-request-example-${example.label}`} className={classes.section}>
                              <Typography variant="body2" className={classes.smallText}>
                                {example.label}
                              </Typography>
                              <pre className={classes.pre}>{formatJson(example.value)}</pre>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className={classes.section}>
                        <Divider />
                        <Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginTop: 16 }}>
                          Responses
                        </Typography>

                        {Object.entries(endpoint.responses).map(([statusCode, response]) => {
                          const resolvedResponse = response.$ref ? resolveRef(spec, response.$ref) : response;
                          const contentEntries = getContentEntries(resolvedResponse?.content);
                          const firstContent = contentEntries[0]?.[1];
                          const responseExamples = getResponseExamples(spec, response);

                          return (
                            <div key={`${endpoint.id}-response-${statusCode}`} className={classes.responseBlock}>
                              <Typography variant="subtitle2">
                                {statusCode} - {resolvedResponse?.description || "Sem descricao"}
                              </Typography>

                              {firstContent?.schema && (
                                <Typography variant="body2" className={classes.smallText}>
                                  Schema: {describeSchema(spec, firstContent.schema)}
                                </Typography>
                              )}

                              {responseExamples.map((example) => (
                                <div key={`${endpoint.id}-response-${statusCode}-${example.label}`}>
                                  {example.contentType && (
                                    <Typography variant="body2" className={classes.smallText}>
                                      {example.label} ({example.contentType})
                                    </Typography>
                                  )}
                                  <pre className={classes.pre}>{formatJson(example.value)}</pre>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </Paper>
                  );
                })}
              </div>
            </div>
          ))}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MessagesAPI;
